/**
 * Exit strategy vocabulary.
 *
 * Every label here was chosen to make one substitution impossible: strategic
 * FIT reading as transaction LIKELIHOOD. A tier called "strong candidate" or
 * "most likely buyer" says something nothing in the evidence supports, so the
 * tiers say `Strong strategic fit`, `Plausible candidate` and `Watchlist`, and
 * each carries an explanation that names what it does not mean.
 *
 * The same care applies to the pathway tiers: `supported` describes how much
 * evidence there is for a route, not how probable that route is.
 */

import type {
  AcquirerFitDimension,
  AcquirerFitTier,
  ExitPathwayName,
  ExitPathwayTier,
  ThesisComparison,
} from '@/lib/strategic-analysis/types';

export const PATHWAY_LABELS: Record<ExitPathwayName, string> = {
  strategic_acquisition: 'Strategic acquisition',
  private_equity_buyout: 'Private-equity buyout',
  merger: 'Merger',
  secondary_sale: 'Secondary sale',
  ipo: 'IPO',
  continued_independence: 'Continued independence',
};

/** All six, always. A pathway that vanished would read as ruled out. */
export const PATHWAY_ORDER: ExitPathwayName[] = [
  'strategic_acquisition',
  'private_equity_buyout',
  'merger',
  'secondary_sale',
  'ipo',
  'continued_independence',
];

export const PATHWAY_TIER_LABELS: Record<ExitPathwayTier, string> = {
  supported: 'Supported by evidence',
  plausible_with_caveats: 'Plausible, with caveats',
  weakly_supported: 'Weakly supported',
  insufficient_evidence: 'Insufficient evidence',
};

export const FIT_TIER_LABELS: Record<AcquirerFitTier, string> = {
  strong_strategic_fit: 'Strong strategic fit',
  plausible_candidate: 'Plausible candidate',
  watchlist: 'Watchlist',
};

/** Each says what the tier is NOT, because that is the reading to prevent. */
export const FIT_TIER_EXPLANATIONS: Record<AcquirerFitTier, string> = {
  strong_strategic_fit:
    'At least three fit dimensions are supported by evidence and none is contradicted. This describes how well the businesses fit, not whether a transaction is likely.',
  plausible_candidate:
    'At least two fit dimensions are supported. This describes fit, not intent — nothing here says this company is considering an acquisition.',
  watchlist:
    'Identity is verified and one dimension is supported, with material gaps. Worth watching; not a shortlist.',
};

export const FIT_DIMENSION_LABELS: Record<AcquirerFitDimension, string> = {
  product_use_case_adjacency: 'Product and use-case adjacency',
  customer_channel_adjacency: 'Customer and channel adjacency',
  geographic_compatibility: 'Geographic compatibility',
  acquisition_partnership_pattern: 'Evidenced acquisition and partnership pattern',
  capacity_integration_feasibility: 'Publicly supportable capacity',
};

export const DRIVER_JUDGEMENT_LABELS: Record<'supports' | 'mixed' | 'weakens' | 'unknown', string> = {
  supports: 'Supports',
  mixed: 'Mixed',
  weakens: 'Weakens',
  unknown: 'No evidence found',
};

export const PATHWAY_DRIVER_LABELS: Record<string, string> = {
  buyer_adjacency: 'Buyer adjacency',
  sector_acquisition_activity: 'Sector acquisition activity',
  integration_value: 'Integration value',
  buyer_capacity: 'Credible buyer capacity',
  revenue_quality: 'Revenue quality',
  profitability_potential: 'Profitability and cash-flow potential',
  operational_scalability: 'Operational scalability',
  sector_sponsor_activity: 'Sector sponsor activity',
  complementary_assets: 'Complementary assets',
  market_access: 'Market access',
  governance_and_economic_compatibility: 'Governance and economic compatibility',
  shareholder_context: 'Shareholder context',
  liquidity_context: 'Liquidity context',
  later_stage_investor_demand: 'Later-stage investor demand',
  scale: 'Scale',
  growth_durability: 'Growth durability',
  governance: 'Governance',
  reporting_maturity: 'Reporting maturity',
  public_market_comparables: 'Public-market comparables',
  financing_and_cash_generation_viability: 'Financing and cash generation',
  durable_economics: 'Durable economics',
  strategic_optionality: 'Strategic optionality',
};

export function pathwayDriverLabel(driver: string): string {
  return PATHWAY_DRIVER_LABELS[driver] ?? driver;
}

export const THESIS_COMPARISON_LABELS: Record<ThesisComparison, string> = {
  aligned: 'Aligned',
  partially_aligned: 'Partially aligned',
  divergent: 'Divergent',
  insufficient_evidence: 'Insufficient evidence to compare',
};

export const THESIS_COMPARISON_EXPLANATIONS: Record<ThesisComparison, string> = {
  aligned: 'The pathways you prefer are the ones the current evidence supports most.',
  partially_aligned: 'Some of the pathways you prefer are supported by the current evidence; others are not.',
  divergent: 'The current evidence points to different pathways than the ones you prefer.',
  insufficient_evidence:
    'There is not enough evidence to say whether your thesis and the assessment agree.',
};

/**
 * The phrase the whole feature is built around.
 *
 * Every judgement in this analysis is prefixed with it. "The most supported
 * pathway is X" is a claim about the world; "Inverio's assessment based on
 * currently available evidence" is a claim about the assessment, which is the
 * only one that is true.
 */
export const ASSESSMENT_QUALIFIER = "Inverio's assessment based on currently available evidence.";
